import capitalize from 'lodash/capitalize';

import { NodeStatusChip, ResourceOfferingChip } from 'components/chips';
import { transformNodeRole } from 'features/cluster/nodes/_utils/common';
import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { Detail } from './Detail';
import { Section } from './Section';
import { getNodeSpecs } from '../../../../_utils/getNodeSpecs';

import { InfoTooltip } from '@cast/design-system';

type Props = {
  node: NodeWithDependencies;
};

const TITLE = 'Overview';

export const OverviewSection = ({ node }: Props) => {
  const specs = getNodeSpecs(node);

  return (
    <Section title={TITLE} testId="node-details-overview-section">
      <Detail
        title="Status:"
        detail={
          <NodeStatusChip
            testId="node-status-chip"
            status={node.state.phase}
            size="small"
          />
        }
      />
      <Detail
        title="Id:"
        detail={node.id}
        showCopy={true}
        copyValue={node.id}
        testId="node-details-id"
      />
      <Detail
        title="Role:"
        detail={
          node.role ? capitalize(transformNodeRole(node.role)) : undefined
        }
        testId="node-details-role"
      />
      <Detail
        title="Instance type:"
        detail={node.instanceType}
        showCopy={true}
        copyValue={node.instanceType}
        testId="node-details-instance-type"
      />
      <Detail title="Spec:" detail={specs} testId="node-details-spec" />

      <Detail
        title={
          <>
            Resource offering
            <InfoTooltip
              title="Resource offering is determined by node labels rather than real-time cloud provider data."
              noDefaultIconStyling={false}
            />
            :
          </>
        }
        detail={
          <ResourceOfferingChip
            resourceOffering={node.resourceOffering}
            size="xs"
          />
        }
        testId="node-details-resource-offering"
      />
      <Detail
        title="Operating system:"
        detail={
          node.nodeInfo?.operatingSystem
            ? capitalize(node.nodeInfo.operatingSystem)
            : undefined
        }
        testId="node-details-operating-system"
      />
      <Detail
        title="Operating system image:"
        detail={node.nodeInfo?.osImage}
        showCopy={true}
        copyValue={node.nodeInfo?.osImage}
        testId="node-details-operating-system-image"
      />
      <Detail
        title="Architecture:"
        detail={node.nodeInfo?.architecture}
        testId="node-details-architecture"
      />
    </Section>
  );
};

const OverviewSectionSkeleton = () => {
  return (
    <Section title={TITLE}>
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
      <Detail.Skeleton />
    </Section>
  );
};

OverviewSection.Skeleton = OverviewSectionSkeleton;
